import React from "react";
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import { useTranslation } from "react-i18next";
import PhoneInput from "../../../../../components/PhoneInput";
import Input from "../../../../../components/Input";
import Switch from "../../../../../components/Switch";
import Dropdown from "../../../../../components/Dropdown";
import TextArea from "../../../../../components/TextArea";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { Validation } from "../../../../../interfaces/common";
import { AddEdit as AddEditInterface } from "../../../../../interfaces/vendor.interface";
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../../../enums';

interface FormDetailsProps {
  serviceTypes: any[];
  cities: any[];
  allServiceTypes: any[];
  allCities: any[];
  formData: AddEditInterface;
  validation: Validation;
  view?: boolean;
  dropdownSearchData: any;
  nameAutocompleteSuggestions?: any[];
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
  onSelect: () => void;
}

const Form: React.FC<FormDetailsProps> = ({
  serviceTypes,
  cities,
  allServiceTypes,
  allCities,
  formData,
  validation,
  view,
  dropdownSearchData,
  nameAutocompleteSuggestions,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange,
  onSelect
}) => {
  const { t } = useTranslation();

  const handleAutocompleteSelect = (suggestion: any) => {
    if (suggestion?.id) {
      const url = `${Modules.resources}${AuthRoutes.vendors}${RouteParamsTypes.view}/${suggestion.id}`;
      window.open(url, '_blank');
    }
    onSelect();
  }

  return (
    <form className="card-body" autoComplete="off">
      <div className="row g-6">
        <h6 className="mb-0">
          1. {t("vendor.addEdit.pageTexts.form.sections.primaryDetails")}
        </h6>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline position-relative">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.name}
              name={VALIDATION_FIELD_MAPPING.name}
              disabled={view}
              error={!!validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.name)}
              errorMessage={validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.name)?.message || ""}
              label={t("vendor.addEdit.pageTexts.form.name.label")}
              placeholder={t("vendor.addEdit.pageTexts.form.name.placeholder")}
              value={formData.name}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.name)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.name)}
              autoComplete="off"
            />
            {nameAutocompleteSuggestions && nameAutocompleteSuggestions.length > 0 && (
              <div className="dropdown-menu show w-100 dropdown-menu-popover-root" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1050, maxHeight: '200px', overflowY: 'auto' }}>
                <ul className="list-unstyled mb-0 dropdown-select-menu-list">
                  {nameAutocompleteSuggestions.map((s: any, index: number) => (
                    <MenuItem
                      key={index}
                      classes={{
                        root: 'dropdown-menu-item',
                        selected: 'dropdown-menu-item-selected',
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAutocompleteSelect(s);
                      }}
                    >
                      <span>{s.name}</span>
                    </MenuItem>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.companyName}
              name={VALIDATION_FIELD_MAPPING.companyName}
              disabled={!!view}
              error={!!validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.companyName)}
              errorMessage={validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.companyName)?.message ?? ""}
              label={t("vendor.addEdit.pageTexts.form.companyName.label")}
              placeholder={t("vendor.addEdit.pageTexts.form.companyName.placeholder")}
              value={formData.companyName}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.companyName)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.companyName)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">
          2. {t("vendor.addEdit.pageTexts.form.sections.serviceDetails")}
        </h6>
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={formData.serviceTypeId ? [formData.serviceTypeId] : []}
              options={(serviceTypes || []).map(serviceType => ({
                label: serviceType.name,
                value: serviceType.id
              }))}
              allOptions={(allServiceTypes || []).map(serviceType => ({
                label: serviceType.name,
                value: serviceType.id
              }))}
              id={VALIDATION_FIELD_MAPPING.serviceType}
              name={VALIDATION_FIELD_MAPPING.serviceType}
              disabled={view}
              searchText={dropdownSearchData.serviceTypeId}
              onSearch={onDropdownSearchChange}
              placeholder={t("vendor.addEdit.pageTexts.form.serviceType.placeholder")}
              label={t("vendor.addEdit.pageTexts.form.serviceType.label")}
              required
              error={!!validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.serviceType)}
              errorMessage={validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.serviceType)?.message ?? ""}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Dropdown
              multiSelect
              values={formData.cities}
              options={(cities || []).map((city: any) => ({
                label: city.name,
                value: city.id
              }))}
              allOptions={(allCities || []).map((city: any) => ({
                label: city.name,
                value: city.id
              }))}
              id={VALIDATION_FIELD_MAPPING.cities}
              name={VALIDATION_FIELD_MAPPING.cities}
              disabled={view}
              searchText={dropdownSearchData.cities}
              placeholder={t("vendor.addEdit.pageTexts.form.cities.placeholder")}
              label={t("vendor.addEdit.pageTexts.form.cities.label")}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onSearch={onDropdownSearchChange}
            />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.serviceRate}
              name={VALIDATION_FIELD_MAPPING.serviceRate}
              disabled={!!view}
              error={!!validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.serviceRate)}
              errorMessage={validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.serviceRate)?.message ?? ""}
              label={t("vendor.addEdit.pageTexts.form.serviceRate.label")}
              placeholder={t("vendor.addEdit.pageTexts.form.serviceRate.placeholder")}
              value={formData.serviceRate}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.serviceRate)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.serviceRate)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">
          3. {t("vendor.addEdit.pageTexts.form.sections.contactDetails")}
        </h6>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              required
              defaultCountry="CA"
              id={VALIDATION_FIELD_MAPPING.phoneNumber}
              name={VALIDATION_FIELD_MAPPING.phoneNumber}
              disabled={view}
              error={!!validation.fields.find(field => field.field === VALIDATION_FIELD_MAPPING.phoneNumber)}
              errorMessage={validation.fields.find(field => field.field === VALIDATION_FIELD_MAPPING.phoneNumber)?.message ?? ""}
              label={t("vendor.addEdit.pageTexts.form.phoneNumber.label")}
              placeholder={t("vendor.addEdit.pageTexts.form.phoneNumber.placeholder")}
              value={(formData.phoneCountryCode || "") + (formData.phoneNumber || "")}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.phoneNumber)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.phoneNumber)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              defaultCountry="CA"
              id={VALIDATION_FIELD_MAPPING.emergencyPhone}
              name={VALIDATION_FIELD_MAPPING.emergencyPhone}
              disabled={view}
              error={!!validation.fields.find(field => field.field === VALIDATION_FIELD_MAPPING.emergencyPhone)}
              errorMessage={validation.fields.find(field => field.field === VALIDATION_FIELD_MAPPING.emergencyPhone)?.message ?? ""}
              label={t("vendor.addEdit.pageTexts.form.emergencyPhone.label")}
              placeholder={t("vendor.addEdit.pageTexts.form.emergencyPhone.placeholder")}
              value={(formData.emergencyPhoneCountryCode || "") + (formData.emergencyPhone || "")}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.emergencyPhone)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.emergencyPhone)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.email}
              name={VALIDATION_FIELD_MAPPING.email}
              disabled={!!view}
              error={!!validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.email)}
              errorMessage={validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.email)?.message ?? ""}
              label={t("vendor.addEdit.pageTexts.form.email.label")}
              placeholder={t("vendor.addEdit.pageTexts.form.email.placeholder")}
              value={formData.email}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.email)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.email)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">
          4. {t("vendor.addEdit.pageTexts.form.sections.otherInformation")}
        </h6>
        <div className="col-md-9">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              disabled={view}
              error={!!validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.notes)}
              errorMessage={validation.fields.find(f => f.field === VALIDATION_FIELD_MAPPING.notes)?.message ?? ""}
              label={t("vendor.addEdit.pageTexts.form.notes.label")}
              placeholder={t("vendor.addEdit.pageTexts.form.notes.placeholder")}
              value={formData.notes}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.notes)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.notes)}
              rows={4}
              className="form-control-lg h-100"
            />
          </div>
        </div>
        <div className="col-md-3 d-flex align-items-start">
          <div className="form-floating form-floating-outline">
            <Switch
              label={formData.status ? t("vendor.addEdit.pageTexts.form.status.active") : t("vendor.addEdit.pageTexts.form.status.inActive")}
              checked={formData.status}
              name={VALIDATION_FIELD_MAPPING.status}
              disabled={view}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.status)}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default Form;